import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";

/**
 * `/projects/:slug/releases/new` — create a release. The release
 * is created in GitLab (as an active milestone) on submit; flip
 * its status afterwards from the edit page.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const Route = createFileRoute("/_app/projects/$slug/releases/new")({
  component: NewReleasePage,
});

function NewReleasePage() {
  const { slug } = Route.useParams();
  const { project } = useLoaderData({ from: "/_app/projects/$slug" });
  const navigate = useNavigate();
  const create = useApiMutation(
    (body: { name: string; description: string | null; dueDate: string | null }) =>
      api.post(`/api/projects/${slug}/releases`, body),
    {
      onSuccess: () => navigate({ to: "/projects/$slug/releases", params: { slug } }),
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't create the release — you may not have permission."
          : "Couldn't create the release.",
    },
  );

  async function createRelease(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) ?? "").trim();
    if (!name) return;
    await create.mutate({
      name,
      description: ((fd.get("description") as string) ?? "").trim() || null,
      dueDate: ((fd.get("dueDate") as string) ?? "").trim() || null,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">New Release</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Add a release to{" "}
            <Link to="/projects/$slug" params={{ slug }} className="text-primary hover:underline">
              {project.name}
            </Link>
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/projects/$slug/releases" params={{ slug }}>
            Cancel
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Release Details</CardTitle>
          <CardDescription>Define a release for this project.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={createRelease} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="e.g. v1.0, Sprint 3, Q2 Release"
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-none">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                placeholder="What is this release about?"
                className={`${INPUT_CLS} min-h-[80px]`}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="dueDate" className="text-sm font-medium leading-none">
                Due Date
              </label>
              <DatePicker id="dueDate" name="dueDate" />
            </div>

            {create.error && <p className="text-sm text-destructive">{create.error}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={create.pending}>
                {create.pending ? "Creating…" : "Create Release"}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/projects/$slug/releases" params={{ slug }}>
                  Cancel
                </Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
